import isInTelegram from '@/utils/isInTelegram';

declare global {
	interface Window {
		dataLayer: Record<string, any>[];
		google_tag_manager?: Record<string, any>;
	}
}

const GTM_ID = import.meta.env.VITE_GTM_ID as string | undefined;

// GA4 measurement ID of the stream whose cookies we read. A domain can serve
// several GA4 streams — reading whichever `_ga_*` cookie comes first would
// report a session id belonging to a different property.
const GA4_MEASUREMENT_ID =
	(import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined) || 'G-N9LC9P317E';

const GA_SESSION_COOKIE = `_ga_${GA4_MEASUREMENT_ID.replace(/^G-/, '')}`;

// GA4 closes a session after 30 minutes of inactivity. The session cookie
// outlives it by two years and is only refreshed when gtag actually ran, so a
// blocked/undelivered gtag leaves a stale id behind — sending it files the
// purchase against a visit that ended weeks ago.
const GA_SESSION_MAX_AGE_SEC = 30 * 60;

function isEnabled(): boolean {
	return !!GTM_ID;
}

export function initGtm(): void {
	if (!isEnabled()) return;

	// GTM script in <head>
	const script = document.createElement('script');
	script.async = true;
	script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
	document.head.insertBefore(script, document.head.firstChild);

	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

	// GTM noscript in <body>
	const noscript = document.createElement('noscript');
	const iframe = document.createElement('iframe');
	iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`;
	iframe.height = '0';
	iframe.width = '0';
	iframe.style.display = 'none';
	iframe.style.visibility = 'hidden';
	noscript.appendChild(iframe);
	document.body.insertBefore(noscript, document.body.firstChild);
}

function pushEvent(data: Record<string, any>): void {
	if (!isEnabled()) return;
	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push(data);
}

export function gtmPageView(pagePath: string): void {
	pushEvent({
		event: 'page_view',
		page_path: pagePath,
	});
}

// How the person authenticated. Where it happened is a separate dimension and
// does not belong here — mixing the two invents sign-in methods that don't exist.
export type GtmLoginMethod = 'telegram' | 'google' | 'email';

/**
 * Which surface the person is on: the Telegram mini-app or the ordinary web.
 *
 * Reported to GA4 as `sale_platform`, never as `source`: there `source`,
 * `medium` and `campaign` are the traffic-acquisition vocabulary, and putting a
 * surface in them would eventually show "traffic from miniapp" in the source
 * reports. Pairs with the existing `sale_channel` (web | bot — is there a
 * browser at all); this one says which web.
 *
 * The invoice request keeps calling the same value `source` — that is our own
 * API, unrelated to GA, and the bot renames it on the way out.
 */
function salePlatform(): 'web' | 'miniapp' {
	try {
		return isInTelegram() ? 'miniapp' : 'web';
	} catch {
		// Storage can be unavailable (private mode) — analytics never blocks auth.
		return 'web';
	}
}

export function gtmLogin(userId: number, method: GtmLoginMethod): void {
	pushEvent({
		event: 'login',
		method,
		user_id: userId,
		sale_platform: salePlatform(),
	});
}

// Registration. The client cannot tell a first login from a returning one on
// its own — it depends on `is_new_user` in the auth response; until the API
// carries that flag every auth keeps emitting `login`. See store/user.ts::setup.
export function gtmSignUp(userId: number, method: GtmLoginMethod): void {
	pushEvent({
		event: 'sign_up',
		method,
		user_id: userId,
		sale_platform: salePlatform(),
	});
}

// The id last announced to GTM. The store re-reads the user from seven places
// (session restore, social connect, returning from payment…), and every one of
// them would otherwise re-announce the same id — a duplicate trigger for
// whatever ends up listening to it. Cleared on sign-out, so the next sign-in is
// announced even when it is the same person: that is a real auth event, not a
// repeat.
let announcedUserId: number | null = null;

// Announces the identified user so GTM can re-run its Google tag with a known
// user_id. The named event is the whole point: a push WITHOUT one updates the
// data-model variable but fires no tag, and the configuration tag — which is
// what actually sets User-ID — has already run by then, on page initialisation.
// That is why User-ID stayed empty for authenticated visitors: the id was
// always published after the only tag that could have read it.
//
// Fire this BEFORE the login/sign_up event so that event carries User-ID too.
export function gtmUserId(userId: number): void {
	if (announcedUserId === userId) return;
	announcedUserId = userId;
	pushEvent({
		event: 'user_id_ready',
		user_id: userId,
	});
}

// Called when the session ends, so a later sign-in announces the id again.
// GTM itself keeps the previous User-ID until the page reloads — that is a
// separate problem, solved in the container, not from here.
export function gtmForgetUserId(): void {
	announcedUserId = null;
}

// Optional fields are pushed as undefined on purpose: undefined overwrites a
// stale value left in the GTM data model by a previous push, and the GA4 tag
// drops undefined params — so the event goes out without the key.

// Fires once the buyer reaches the payment-method step of the checkout, which
// is what the ad-account goal is meant to learn from. It deliberately does NOT
// fire on the cabinet's renew/expired buttons any more: those count renewals,
// not started checkouts, and mixing both under one name taught the goal the
// wrong signal.
export interface GtmBeginCheckout {
	plan?: string;
	value?: number;
	currency?: string;
	checkoutSource?: string;
	coupon?: string;
}

export function gtmBeginCheckout(data: GtmBeginCheckout = {}): void {
	pushEvent({
		event: 'begin_checkout',
		plan: data.plan,
		value: data.value,
		currency: data.currency,
		checkout_source: data.checkoutSource,
		coupon: data.coupon,
	});
}

// Fires on the "Pay" click, when the payment method is already chosen.
export interface GtmAddPaymentInfo {
	plan: string;
	paymentMethod: string;
	value?: number;
	currency?: string;
	coupon?: string;
}

export function gtmAddPaymentInfo(info: GtmAddPaymentInfo): void {
	pushEvent({
		event: 'add_payment_info',
		plan: info.plan,
		value: info.value,
		currency: info.currency,
		payment_method: info.paymentMethod,
		coupon: info.coupon,
	});
}

// Fires when the user successfully applies a promo code in the promo input.
export function gtmPromoApply(coupon: string): void {
	pushEvent({
		event: 'promo_apply',
		coupon,
	});
}

// ─── GA4 identifiers attached to the invoice ────────────────────────────────
// The purchase event is server-side only (Measurement Protocol from the bot).
// It needs the buyer's client_id to land in the right user/session, so the
// checkout reads the GA cookies and stores them on the bill. The diagnostic
// fields alongside them explain WHY an identifier is missing, which is
// otherwise invisible once the purchase is already unattributed.

export type GaStatus = 'ok' | 'stale_session' | 'no_session' | 'no_client' | 'gtm_absent';

export interface GtmClientInfo {
	clientId?: string;
	sessionId?: string;
	gaStatus: GaStatus;
	sessionAgeSec?: number;
	gtmLoaded: boolean;
	capturedAt: string;
	source: 'web' | 'miniapp';
	pageLocation: string;
}

/**
 * Every value stored under `name`, in document.cookie order.
 *
 * Splits on a bare ';' and trims: `split('; ')` leaves a cookie glued to the
 * previous pair whenever a separator arrives without its space, and the glued
 * name then matches nothing. decodeURIComponent is applied per cookie and only
 * to the one asked for, because an unrelated cookie holding a stray '%'
 * (`promo=50%off`) throws URIError — which used to abort the whole read.
 */
function readCookieValues(name: string): string[] {
	let raw = '';
	try {
		raw = document.cookie || '';
	} catch {
		return [];
	}

	const values: string[] = [];
	for (const part of raw.split(';')) {
		const pair = part.trim();
		const eq = pair.indexOf('=');
		if (eq <= 0) continue;
		if (pair.slice(0, eq) !== name) continue;

		const encoded = pair.slice(eq + 1);
		let value = encoded;
		try {
			value = decodeURIComponent(encoded);
		} catch {
			// Malformed escape — keep the raw value rather than losing the cookie.
		}
		if (value) values.push(value);
	}
	return values;
}

/**
 * client_id from `_ga`: GA<version>.<domain depth>.<random>.<first visit ts>.
 *
 * The shape is matched strictly. A truncated or emptied `_ga` (what cookie
 * cleaners and consent scripts leave behind) must not pass as an identifier:
 * a bogus client_id is worse than none, because the bot then files the
 * purchase under a user that does not exist.
 */
function parseClientId(): string | undefined {
	for (const value of readCookieValues('_ga')) {
		const match = value.match(/^GA\d+\.\d+\.(\d+\.\d+)$/);
		if (match) return match[1];
	}
	return undefined;
}

interface GaSession {
	sessionId: string;
	lastActivity: number;
}

/**
 * session id + last-activity mark from `_ga_<stream>`.
 *
 *   GS2.1.s<session_id>$o1$g0$t<last activity>$j60$l0$h0   (current)
 *   GS1.1.<session_id>.<n>.<engaged>.<last activity>.…      (legacy)
 *
 * Both marks are unix seconds. When a format carries no activity mark, the
 * session start doubles as one — erring towards "stale" is the safe direction.
 */
function parseSession(): GaSession | undefined {
	for (const value of readCookieValues(GA_SESSION_COOKIE)) {
		const modern = value.match(/^GS[2-9]\d*\.\d+\.s(\d+)/);
		if (modern) {
			const activity = value.match(/\$t(\d+)/);
			return {
				sessionId: modern[1],
				lastActivity: Number(activity ? activity[1] : modern[1]),
			};
		}

		const legacy = value.match(/^GS1\.\d+\.(\d+)(?:\.\d+){2}\.(\d+)/);
		if (legacy) {
			return { sessionId: legacy[1], lastActivity: Number(legacy[2]) };
		}

		const bare = value.match(/^GS\d+\.\d+\.s?(\d+)/);
		if (bare) {
			return { sessionId: bare[1], lastActivity: Number(bare[1]) };
		}
	}
	return undefined;
}

// utm_* stay — attribution rides on them. Anything that identifies the buyer
// or authenticates them is dropped: page_location travels into analytics,
// logs and Referer.
const SENSITIVE_QUERY_PARAMS = ['email', 'data', 'token', 'handoff', 'code'];

function currentPageLocation(): string {
	try {
		const url = new URL(window.location.href);
		for (const param of SENSITIVE_QUERY_PARAMS) {
			url.searchParams.delete(param);
		}
		url.hash = '';
		return url.toString();
	} catch {
		return '';
	}
}

function collectClientInfo(): GtmClientInfo {
	const gtmLoaded = !!window.google_tag_manager;
	const info: GtmClientInfo = {
		gaStatus: 'ok',
		gtmLoaded,
		capturedAt: new Date().toISOString(),
		source: salePlatform(),
		pageLocation: currentPageLocation(),
	};

	const clientId = parseClientId();
	const session = parseSession();
	const sessionAgeSec = session
		? Math.max(0, Math.floor(Date.now() / 1000) - session.lastActivity)
		: undefined;

	if (clientId) info.clientId = clientId;
	if (sessionAgeSec !== undefined) info.sessionAgeSec = sessionAgeSec;

	const isFresh = sessionAgeSec !== undefined && sessionAgeSec <= GA_SESSION_MAX_AGE_SEC;

	// Most fundamental cause first: no GTM explains every missing identifier
	// below it, so reporting it as `no_client` would hide the real reason.
	if (!gtmLoaded) {
		info.gaStatus = 'gtm_absent';
	} else if (!clientId) {
		info.gaStatus = 'no_client';
	} else if (!session) {
		info.gaStatus = 'no_session';
	} else if (!isFresh) {
		info.gaStatus = 'stale_session';
	}

	// session_id only ships paired with a client_id and while the session is
	// still open. On its own it is worse than nothing: the bot falls back to a
	// synthetic client_id, GA4 opens a brand-new session around it, and the
	// server purchase stops deduplicating against anything.
	if (clientId && session && isFresh) {
		info.sessionId = session.sessionId;
	}

	return info;
}

/**
 * The `$t` activity mark of the current GA4 session, or undefined when there is
 * no session cookie yet.
 *
 * Refreshed on every hit, and a hit only leaves after gtag has accepted an
 * incoming `_gl` — which makes a CHANGE in this value proof that the container
 * ran AND the linker parameter was unpacked. Used by the OAuth entry point;
 * see the comment on waitForGaIdentity for why nothing cheaper works.
 */
export function gtmSessionActivity(): number | undefined {
	try {
		return parseSession()?.lastActivity;
	} catch {
		return undefined;
	}
}

export function gtmClientInfo(): GtmClientInfo {
	try {
		return collectClientInfo();
	} catch {
		// Analytics must never block a payment.
		return {
			gaStatus: 'gtm_absent',
			gtmLoaded: false,
			capturedAt: new Date().toISOString(),
			source: 'web',
			pageLocation: '',
		};
	}
}
